import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { categories } from "@/mocks/categories";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://kakobuypolska.com";

const spreadsheetJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Kakobuy Spreadsheet - Największa Baza Online",
  description: "Pełna baza produktów z Chin z gotowymi linkami, kategoriami i zdjęciami QC. Przeglądaj, filtruj i kupuj — wszystko w jednym miejscu.",
  url: `${siteUrl}/kakobuy-spreadsheet`,
  isPartOf: {
    "@type": "WebSite",
    name: "Kakobuy Spreadsheet Polska",
    url: siteUrl,
  },
};

export default function KakobuySpreadsheetPage() {
  return (
    <div className="min-h-[100dvh] flex flex-col">
      <JsonLd data={spreadsheetJsonLd} />
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="relative min-h-[50dvh] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20ecommerce%20dashboard%20spreadsheet%20interface%20on%20laptop%20screen%20with%20product%20grid%20minimal%20workspace%20warm%20natural%20lighting%20clean%20aesthetic%20editorial%20photography%20soft%20shadows%20neutral%20tones%20high%20detail&width=1920&height=800&seq=landing-v2-hero&orientation=landscape&nocache=true"
              alt="Kakobuy Spreadsheet"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/60 to-foreground-950/40" />
          </div>
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-16">
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4">
                Kakobuy Spreadsheet
                <br />
                <span className="text-primary-300">Największa Baza Online</span>
              </h1>
              <p className="text-base md:text-lg text-background-200/90 max-w-xl mb-6 leading-relaxed">
                Pełna baza produktów z Chin z gotowymi linkami, kategoriami i zdjęciami QC.
                Przeglądaj, filtruj i kupuj — wszystko w jednym miejscu.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-base font-semibold px-8 py-3.5 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Przejdź do bazy produktów
                <i className="ri-external-link-line" />
              </a>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
              Kategorie produktów
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex items-center gap-4 bg-background-100 rounded-xl p-4 md:p-5 border border-background-200/60 hover:border-primary-300/40 hover:shadow-sm transition-all"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className="ri-shopping-bag-3-line text-xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors truncate">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      Zobacz produkty
                    </p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 transition-colors" />
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Safety */}
        <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
              Bezpieczeństwo zakupów na Kakobuy
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {[
                {
                  icon: "ri-shield-check-line",
                  title: "Weryfikacja sprzedawców",
                  desc: "Sprawdzaj ratingi i opinie sprzedawców przed zakupem. Unikaj tych z niską reputacją.",
                },
                {
                  icon: "ri-image-line",
                  title: "QC zdjęcia",
                  desc: "Zawsze żądaj zdjęć QC przed wysyłką. To Twój sposób na weryfikację jakości.",
                },
                {
                  icon: "ri-secure-payment-line",
                  title: "Bezpieczna płatność",
                  desc: "Kakobuy działa jako agent zakupowy. Pieniądze są bezpieczne do momentu zatwierdzenia.",
                },
              ].map((item) => (
                <div key={item.title} className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/60">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-4">
                    <i className={`${item.icon} text-xl`} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
              Gotowy na zakupy?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mb-8">
              Przejdź do pełnej bazy Kakobuy Spreadsheet i znajdź swoje ulubione produkty już teraz.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-base font-semibold px-8 py-3.5 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Kakobuy Spreadsheet
                <i className="ri-external-link-line" />
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-background-100 text-foreground-950 text-base font-medium px-8 py-3.5 rounded-full border border-background-300 hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                Buy it now
                <i className="ri-shopping-bag-line" />
              </a>
            </div>
            <p className="text-xs text-foreground-500 mt-6">
              Wróć na{" "}
              <Link to="/" className="text-primary-500 hover:underline">
                stronę główną
              </Link>
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}